/**
 * useChatSubmit — the chat composer submit handler extracted from ChatBox.
 *
 * `handleSubmit` was a plain async function recreated on every render (closing
 * over the latest ChatBox state). This hook preserves those exact semantics: it
 * is called once per render with a fresh `deps` object and returns a fresh
 * handler each time, so the closure always reads the latest values. The body is
 * reproduced VERBATIM from ChatBox — slash-command routing, the three intent
 * fast-paths, conversation-context assembly (sent once per conversation via
 * conversationContextSentRef), auto-rename, attachment encoding (image/doc/text),
 * the streaming re-entrancy queue (messageQueueRef), and the post-send cleanup.
 *
 * Refs are threaded by reference so reads/writes stay shared with ChatBox.
 */
import { parseInput, getHelpText, cardHorizonFor } from '@/features/chat/utils/command-parser';
import { extractMentionReferences, stripMentionMarkers } from '@/features/chat/utils/mention-parser';
import { RESULT_CARDS_MARKER } from '@/features/chat/components/result-cards/types';
import { deriveTitleFromMessage } from '@/features/chat/utils/chat-text';
import { fileToBase64, compressImageForVision } from '@/features/chat/utils/chat-attachments';
import { isNewChatPlaceholder, setLastMessageTimestamp } from '@/lib/greetings';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { ENABLE_LOCAL_VIEW_INTENT_FAST_PATH } from '../components/chatbox/constants';
import type { MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { ChatAttachment, ChatMessage } from '@/features/chat/hooks/useChat';
import type { Conversation } from '@/types/conversations';
import { useChatAgentScope } from '@/app/providers/ChatAgentScopeContext';
import { resolveSendConversationId } from './resolveSendTarget';

type QueuedMessage = {
  content: string;
  conversationId: string;
  context?: any;
  images?: Array<{ mediaType: string; data: string; filename: string }>;
  documents?: Array<{ mediaType: string; data: string; filename: string }>;
  attachments?: ChatAttachment[];
  pastedAttachments?: Array<{ content: string; label: string; size: number }>;
};

type PendingAttachmentLike = {
  file?: File | null;
  filename: string;
  fileType: string;
  assetId?: string;
  assetUrl?: string;
  previewUrl?: string;
};

type AddLocalMessages = (
  userContent: string,
  assistantContent: string,
  conversationId?: string,
  metadata?: { routing?: string; durationMs?: number; intent?: string },
) => { userMessageId?: string; assistantMessageId: string };

export interface UseChatSubmitDeps {
  isListening: boolean;
  stopDictation: () => void;
  pendingAttachments: PendingAttachmentLike[];
  pastedAttachments: Array<{ content: string; label: string; size: number }>;
  addLocalMessages: AddLocalMessages;
  clearChatAndCloseModulePane: () => void;
  openModal: () => void;
  handleConversationCommand: () => void;
  handleConversationsCommand: () => void;
  handleSearchCommand: (query: string) => Promise<void> | void;
  handleTaskCommand: (input: string) => Promise<void> | void;
  handleNoteCommand: (input: string) => Promise<void> | void;
  handleContactCommand: (input: string) => Promise<void> | void;
  handleEventCommand: (input: string) => Promise<void> | void;
  handleFilterIntent: (content: string) => boolean;
  handleOpenObjectIntent: (content: string) => boolean;
  handleViewIntent: (content: string) => boolean;
  activeConversation: Conversation | null;
  conversationContextSentRef: MutableRefObject<string | null>;
  conversationJson: any;
  messages: ChatMessage[];
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  selectedEditSections: Array<{ sectionId: string; label: string }>;
  isStreaming: boolean;
  messageQueueRef: MutableRefObject<QueuedMessage[]>;
  sendMessage: (
    content: string,
    conversationId?: string,
    mode?: undefined,
    context?: any,
    options?: any,
  ) => unknown;
  lastSentRef: MutableRefObject<{ text: string; at: number } | null>;
  recentlySentFilesRef: MutableRefObject<Map<string, File>>;
  setPendingAttachments: (value: any[]) => void;
  setPastedAttachments: (value: any[]) => void;
  setExpandedPastedAttachmentIds: (value: Set<string>) => void;
  setInputValue: (value: string) => void;
  setSelectedEditSections: (value: Array<{ sectionId: string; label: string }>) => void;
  chatInputRef: MutableRefObject<HTMLTextAreaElement | null>;
  /** Close the @-mention autocomplete on submit (issue #1457). The composer is
   *  cleared with a programmatic setInputValue('') that does NOT fire the
   *  textarea onChange, so without this the dropdown lingers with stale rows
   *  after send — and clicking a stale row would splice a marker into the
   *  freshly-cleared input via the now-stale active-mention span. */
  closeMentionDropdown: () => void;
}

export function useChatSubmit(deps: UseChatSubmitDeps): (e: React.FormEvent) => Promise<void> {
  const {
    isListening,
    stopDictation,
    pendingAttachments,
    pastedAttachments,
    addLocalMessages,
    clearChatAndCloseModulePane,
    openModal,
    handleConversationCommand,
    handleConversationsCommand,
    handleSearchCommand,
    handleTaskCommand,
    handleNoteCommand,
    handleContactCommand,
    handleEventCommand,
    handleFilterIntent,
    handleOpenObjectIntent,
    handleViewIntent,
    activeConversation,
    conversationContextSentRef,
    conversationJson,
    messages,
    token,
    workspaceId,
    queryClient,
    selectedEditSections,
    isStreaming,
    messageQueueRef,
    sendMessage,
    lastSentRef,
    recentlySentFilesRef,
    setPendingAttachments,
    setPastedAttachments,
    setExpandedPastedAttachmentIds,
    setInputValue,
    setSelectedEditSections,
    chatInputRef,
    closeMentionDropdown,
  } = deps;

  // Scoped chat: when the right-rail chat is scoped to an agent/run, route the
  // send to that conversation. Null/unscoped (or no conversationId) is
  // byte-identical to before — see resolveSendConversationId.
  const { scope } = useChatAgentScope();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Dismiss the @-mention autocomplete on every submit path (issue #1457).
    // Done first so it fires whether we send, early-return on a slash command,
    // or bail on empty input — the composer is cleared programmatically below,
    // which won't re-trigger detection to close it. Also clears the stale
    // active-mention span so a late click on a leftover row can't inject a
    // marker into the emptied input.
    closeMentionDropdown();

    if (isListening) {
      stopDictation();
    }

    const formData = new FormData(e.target as HTMLFormElement);
    const content = formData.get('message') as string;

    // Allow send if there are pending attachments (even without text)
    // — image/file attachments OR a pasted-content chip each count as
    // enough to submit without typed input.
    if (
      !content?.trim()
      && pendingAttachments.length === 0
      && pastedAttachments.length === 0
    ) {
      return;
    }

    // If no text but files attached, use a descriptor that NAMES the file(s).
    // Previously this was just "[file]" or "[photo]" which left zero context
    // in chat history — the agent on a follow-up turn saw "[file]" with no
    // way to know what was attached. Including filenames in the visible
    // content means the agent can recover at least minimal context even if
    // structured attachment metadata is missing on later turns.
    const hasImages = pendingAttachments.some(a => a.fileType?.startsWith('image/'));
    const fileDescriptor = (() => {
      if (pendingAttachments.length === 0) return '';
      const namedFiles = pendingAttachments
        .map(a => a.filename)
        .filter((n): n is string => typeof n === 'string' && n.length > 0);
      if (namedFiles.length === 0) return hasImages ? '[photo]' : '[file]';
      const head = namedFiles.slice(0, 3).join(', ');
      const tail = namedFiles.length > 3 ? ` +${namedFiles.length - 3} more` : '';
      return hasImages ? `[photo: ${head}${tail}]` : `[attached: ${head}${tail}]`;
    })();
    const rawContent = content?.trim() || (pendingAttachments.length > 0
      ? fileDescriptor
      : (pastedAttachments.length > 0 ? '[pasted content]' : ''));

    // @-mention references (issue #1457). Extract the structured objects from the
    // raw marker text, then strip the markers so the user turn reads naturally
    // (`@Title` instead of `@[Title](type:id)`). The refs ride on
    // `context.mentionedObjects` and the agent fetches full details via its tools.
    const mentionedObjects = extractMentionReferences(rawContent);
    const trimmedContent = mentionedObjects.length > 0
      ? stripMentionMarkers(rawContent)
      : rawContent;

    const parsed = parseInput(trimmedContent);

    if (parsed.type === 'command') {
      switch (parsed.command) {
        // Phase 1: Client-side commands
        case 'help':
          addLocalMessages('/help', getHelpText(), activeConversation?.id, {
            routing: 'command:help',
            durationMs: 0,
          });
          break;

        case 'clear':
          clearChatAndCloseModulePane();
          break;

        case 'new':
          clearChatAndCloseModulePane();
          break;

        case 'feedback':
          openModal();
          break;

        // Phase 2: Conversation & Search commands
        case 'conversation':
          handleConversationCommand();
          break;

        case 'conversations':
          handleConversationsCommand();
          break;

        case 'search':
          handleSearchCommand(parsed.args);
          break;

        // Phase 3: Quick Create commands
        case 'task':
          handleTaskCommand(parsed.args);
          break;

        case 'note':
          handleNoteCommand(parsed.args);
          break;

        case 'contact':
          handleContactCommand(parsed.args);
          break;

        case 'event':
          handleEventCommand(parsed.args);
          break;

        // Agent commands — synthesize a prompt and send it as a real agent message.
        // These bypass attachment/context assembly and call sendMessage directly.
        case 'contacts':
        case 'places':
        case 'tasks':
        case 'pages':
        case 'drive':
        case 'inbox':
        case 'draft':
        case 'find':
        case 'report':
        case 'who':
        case 'prep':
        case 'brief': {
          if (!activeConversation) break;
          const agentArgs = parsed.args;
          let agentPrompt: string;
          if (parsed.command === 'contacts') {
            agentPrompt = agentArgs ? `Find contacts matching "${agentArgs}"` : 'Show me my contacts — who I communicate with most, and any I should reconnect with.';
          } else if (parsed.command === 'places') {
            agentPrompt = 'Show me my places — restaurants, hotels, and spots I\'ve saved or visited.';
          } else if (parsed.command === 'tasks') {
            agentPrompt = 'Show me my tasks — what\'s open, what\'s due soon, and what I\'ve completed recently.';
          } else if (parsed.command === 'pages') {
            agentPrompt = agentArgs ? `Create a report about ${agentArgs}` : 'Show me my Studio — pages, decks, and reports.';
          } else if (parsed.command === 'drive') {
            agentPrompt = agentArgs ? `Find "${agentArgs}" in my files` : 'Show me my files and documents.';
          } else if (parsed.command === 'inbox') {
            agentPrompt = 'Triage my inbox — what needs my attention?';
          } else if (parsed.command === 'draft') {
            agentPrompt = agentArgs
              ? `Draft an email ${agentArgs}`
              : 'Draft an email — ask me who it\'s for and what it should say.';
          } else if (parsed.command === 'find') {
            if (!agentArgs) {
              addLocalMessages(
                '/find',
                'Usage: `/find` <what you\'re looking for>',
                activeConversation?.id,
                { routing: 'command:find:usage', durationMs: 0 },
              );
              break;
            }
            agentPrompt = `Find ${agentArgs}`;
          } else if (parsed.command === 'report') {
            agentPrompt = `Create a report about ${agentArgs || 'me'}`;
          } else if (parsed.command === 'prep') {
            agentPrompt = agentArgs ? `Prep me for ${agentArgs}` : 'Prep me for my next meeting';
          } else if (parsed.command === 'brief') {
            agentPrompt = 'Give me a briefing — what moved overnight in my mail and calendar?';
          } else {
            // who
            agentPrompt = agentArgs ? `Tell me about ${agentArgs}` : 'Who are my top contacts?';
          }
          sendMessage(agentPrompt, activeConversation.id, undefined, undefined, undefined);
          break;
        }

        // "Your inner circle" — closest people + going quiet.
        case 'circle': {
          addLocalMessages(
            '/circle',
            '\n' + RESULT_CARDS_MARKER + JSON.stringify([{ kind: 'circle', data: {} }]),
            activeConversation?.id,
            { routing: 'command:view-circle', durationMs: 0 },
          );
          break;
        }

        // The shareable archetype card.
        case 'profile': {
          addLocalMessages(
            '/profile',
            '\n' + RESULT_CARDS_MARKER + JSON.stringify([{ kind: 'persona', data: {} }]),
            activeConversation?.id,
            { routing: 'command:view-profile', durationMs: 0 },
          );
          break;
        }

        // Social presence — X, LinkedIn, recent web mentions.
        case 'social': {
          addLocalMessages(
            '/social',
            '\n' + RESULT_CARDS_MARKER + JSON.stringify([{ kind: 'social', data: {} }]),
            activeConversation?.id,
            { routing: 'command:view-social', durationMs: 0 },
          );
          break;
        }

        // "Where you eat" — the reservations report card.
        case 'food': {
          addLocalMessages(
            '/food',
            '\n' + RESULT_CARDS_MARKER + JSON.stringify([{ kind: 'reservations', data: {} }]),
            activeConversation?.id,
            { routing: 'command:view-food', durationMs: 0 },
          );
          break;
        }

        // The travel report card — renders from already-scanned calendar data.
        case 'travel': {
          addLocalMessages(
            '/travel',
            '\n' + RESULT_CARDS_MARKER + JSON.stringify([{ kind: 'travel', data: {} }]),
            activeConversation?.id,
            { routing: 'command:view-travel', durationMs: 0 },
          );
          break;
        }

        case 'schedule':
        case 'today':
        case 'week': {
          const horizon = cardHorizonFor(parsed.command);
          addLocalMessages(
            `/${parsed.command}`,
            '\n' + RESULT_CARDS_MARKER + JSON.stringify([{ kind: 'today', data: { horizon } }]),
            activeConversation?.id,
            { routing: `command:view-${parsed.command}`, durationMs: 0 },
          );
          break;
        }
      }

      setInputValue('');
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.style.height = 'auto';
          chatInputRef.current.focus();
        }
      }, 0);
      return;
    }

    if (handleFilterIntent(trimmedContent)) {
      setInputValue('');
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.style.height = 'auto';
          chatInputRef.current.focus();
        }
      }, 0);
      return;
    }

    // Fast-path: "open [object name]" when a module with matching objects is active
    if (handleOpenObjectIntent(trimmedContent)) {
      setInputValue('');
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.style.height = 'auto';
          chatInputRef.current.focus();
        }
      }, 0);
      return;
    }

    // Optional deterministic fast-path for simple view commands.
    // Disabled by default so module resolution is model-driven and context-aware.
    if (ENABLE_LOCAL_VIEW_INTENT_FAST_PATH && handleViewIntent(trimmedContent)) {
      setInputValue('');
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.style.height = 'auto';
          chatInputRef.current.focus();
        }
      }, 0);
      return;
    }

    if (!activeConversation) return;

    // NOTE: Deterministic query engine disabled for now - too many edge cases
    // Going straight to Claude with all conversation data passed as context for fast responses

    // Send all queries to Claude
    // OPTIMIZATION: Only send conversation data context on FIRST message of the session
    // Claude keeps it in context for follow-up questions, so no need to resend
    let context: any = undefined;

    if (activeConversation) {
      // Detect if this is a write operation (needs tools) vs read query (can use context)
      const isWriteOperation = /\b(add|create|update|edit|delete|remove|schedule|make|draft|write|compose|send|set|change|modify)\b/i.test(trimmedContent);

      // Only send context if:
      // 1. Not a write operation (those need tools anyway)
      // 2. We haven't already sent context for this conversation in this session
      // 3. We have conversation data to send
      const conversationContextKey = `${activeConversation.id}:api`;
      const shouldSendContext = !isWriteOperation &&
        conversationContextSentRef.current !== conversationContextKey &&
        conversationJson?.objects;

      if (shouldSendContext) {
        const conversationData: any = {
          conversationName: conversationJson.conversation?.name || activeConversation.name,
        };

        const stats = conversationJson.objects.stat;
        if (stats && stats.length > 0) {
          conversationData.stats = stats.map((s: any) => ({
            metric: s.title || s.name,
            value: s.value,
            unit: s.unit,
            date: s.recordedAt || s.createdAt,
            notes: s.notes,
          }));
        }

        const tasks = conversationJson.objects.task;
        if (tasks && tasks.length > 0) {
          conversationData.tasks = tasks.map((t: any) => ({
            title: t.title,
            status: t.status,
            priority: t.priority,
            dueDate: t.dueAt,
            description: t.description,
          }));
        }

        const contacts = conversationJson.objects.contact;
        if (contacts && contacts.length > 0) {
          conversationData.contacts = contacts.map((c: any) => ({
            name: c.name || c.title,
            email: c.email,
            phone: c.phone,
            company: c.company,
            role: c.role,
          }));
        }

        const events = conversationJson.objects.event;
        if (events && events.length > 0) {
          conversationData.events = events.map((e: any) => ({
            title: e.title,
            startTime: e.startAt,
            endTime: e.endAt,
            location: e.location,
            description: e.description,
          }));
        }

        const notes = conversationJson.objects.note;
        if (notes && notes.length > 0) {
          conversationData.notes = notes.map((n: any) => ({
            title: n.title,
            content: n.content?.substring(0, 500),
            createdAt: n.createdAt,
          }));
        }

        const places = conversationJson.objects.place;
        if (places && places.length > 0) {
          conversationData.places = places.map((p: any) => ({
            id: p.id,
            name: p.name || p.title,
            address: p.address,
            category: p.category,
            rating: p.rating,
            status: p.status,
          }));
        }

        // Include user memories (AI-learned facts about the user)
        const memories = conversationJson.objects.memory;
        if (memories && memories.length > 0) {
          conversationData.memories = memories.map((m: any) => ({
            content: m.content,
            category: m.category,
            sourceConversation: m.sourceConversationName,
          }));
        }

        // Only set context if we have some data
        const hasData = Object.keys(conversationData).some(k =>
          k !== 'conversationName' &&
          k !== 'conversationType' &&
          conversationData[k]?.length > 0
        );

        if (hasData) {
          context = {
            type: 'conversation_data',
            description: 'Conversation snapshot for context. Prefer this data; call tools if anything is missing.',
            data: conversationData,
            summary: conversationJson.summary,
            fetchedAt: conversationJson.fetchedAt,
          };

          conversationContextSentRef.current = conversationContextKey;
        }
      }
    }

    // Auto-rename: if this is the first user message in a placeholder-named chat,
    // immediately rename it based on message content (fire-and-forget)
    if (
      activeConversation?.id &&
      isNewChatPlaceholder(activeConversation.name) &&
      !messages.some(m => m.role === 'user')
    ) {
      const newTitle = deriveTitleFromMessage(trimmedContent);
      void api.workspaces.updateConversation(token ?? null, workspaceId, activeConversation.id, { title: newTitle })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
        })
        .catch(() => { /* non-critical, title stays as placeholder */ });
    }

    // Convert any pending image attachments to base64 for vision (compressed)
    const imageAttachments: Array<{ mediaType: string; data: string; filename: string }> = [];
    const imageFiles = pendingAttachments.filter(att => att.file && att.fileType.startsWith('image/'));
    for (const att of imageFiles) {
      try {
        const compressed = await compressImageForVision(att.file!);
        imageAttachments.push({ mediaType: compressed.mediaType, data: compressed.data, filename: att.filename });
      } catch {
        // Fall back to raw base64 if compression fails
        try {
          const base64 = await fileToBase64(att.file!);
          imageAttachments.push({ mediaType: att.fileType, data: base64, filename: att.filename });
        } catch {
          console.warn('[ChatBox] Failed to encode image for vision:', att.filename);
        }
      }
    }

    // Convert PDF/document attachments to base64 for agent (max 10MB)
    const documentAttachments: Array<{ mediaType: string; data: string; filename: string }> = [];
    const docFiles = pendingAttachments.filter(att => att.file && att.fileType === 'application/pdf' && att.file.size <= 10 * 1024 * 1024);
    for (const att of docFiles) {
      try {
        const base64 = await fileToBase64(att.file!);
        documentAttachments.push({ mediaType: att.fileType, data: base64, filename: att.filename });
      } catch {
        console.warn('[ChatBox] Failed to encode document:', att.filename);
      }
    }

    // For text-shaped attachments (CSV, VCF, JSON, plain text, markdown,
    // ICS calendar) the agent needs the actual content, not just the
    // filename. PDFs go via documentAttachments base64 (Claude renders them
    // natively); text formats don't have a native block type, so we ride
    // the existing pasted-content rail — the server already injects those
    // into the prompt's <pasted-content> block. Cap each file at ~256KB
    // raw to stay within prompt budget; bigger files just get the metadata
    // treatment as before.
    const TEXT_INLINE_BYTES = 256 * 1024;
    const textShapedAttachments: Array<{ content: string; label: string; size: number }> = [];
    const textFileRegex = /\.(csv|tsv|vcf|json|ndjson|md|txt|log|ics|yaml|yml|xml)$/i;
    const textMimeRegex = /^(text\/|application\/(json|xml|x-yaml|x-ndjson))/i;
    const textFiles = pendingAttachments.filter(att =>
      att.file
      && att.file.size <= TEXT_INLINE_BYTES
      && (textFileRegex.test(att.filename || '') || textMimeRegex.test(att.fileType || ''))
      // Skip ones already handled by image/document paths above
      && !att.fileType?.startsWith('image/')
      && att.fileType !== 'application/pdf'
    );
    for (const att of textFiles) {
      try {
        const text = await att.file!.text();
        textShapedAttachments.push({
          content: text,
          label: att.filename || 'file',
          size: att.file!.size,
        });
      } catch {
        console.warn('[ChatBox] Failed to read text file:', att.filename);
      }
    }

    const attachments: ChatAttachment[] = pendingAttachments.map(att => ({
      filename: att.filename,
      fileType: att.fileType,
      assetId: att.assetId,
      url: att.assetUrl,
      previewUrl: att.previewUrl,
    }));

    // Snapshot pasted-content chips for the outbound request. These are
    // forwarded as a structured field (`pastedAttachments`) on the chat
    // request — NEVER inlined into the user message text — so the server
    // puts them in the attachment block of the prompt, not the user turn.
    // Text-shaped file attachments (CSV/VCF/JSON/...) read above are
    // tacked onto the same payload so the agent sees their content via
    // the existing pasted-content path, not just metadata.
    const pastedAttachmentsPayload = [
      ...pastedAttachments.map(att => ({
        content: att.content,
        label: att.label,
        size: att.size,
      })),
      ...textShapedAttachments,
    ];

    const sendOptions = {
      ...(imageAttachments.length > 0 ? { images: imageAttachments } : {}),
      ...(documentAttachments.length > 0 ? { documents: documentAttachments } : {}),
      ...(attachments.length > 0 ? { attachments } : {}),
      ...(pastedAttachmentsPayload.length > 0 ? { pastedAttachments: pastedAttachmentsPayload } : {}),
      // #1457: when mentions are present, the agent gets the stripped text
      // (trimmedContent) but the bubble shows the marker form so MentionText
      // renders pills.
      ...(mentionedObjects.length > 0 ? { displayContent: rawContent } : {}),
    };

    // Attach selected edit sections to context so the agent knows which parts to edit
    if (selectedEditSections.length > 0) {
      context = {
        ...(context || {}),
        editSections: selectedEditSections,
      };
    }

    // Attach @-mentioned objects (issue #1457) so x-agent injects them into the
    // prompt's context tier. Shape = MentionReference[] ({id,type,title}); the
    // agent fetches full details via its CRUD/search tools.
    if (mentionedObjects.length > 0) {
      context = {
        ...(context || {}),
        mentionedObjects,
      };
    }

    // #1457: marker form to display (pills) vs stripped text for the agent.
    const displayForBubble = mentionedObjects.length > 0 ? rawContent : trimmedContent;

    // If currently streaming, queue the message for later
    if (isStreaming) {
      messageQueueRef.current.push({
        content: trimmedContent,
        conversationId: activeConversation.id,
        context,
        images: imageAttachments.length > 0 ? imageAttachments : undefined,
        documents: documentAttachments.length > 0 ? documentAttachments : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        pastedAttachments: pastedAttachmentsPayload.length > 0 ? pastedAttachmentsPayload : undefined,
      });
      // Show the queued message in UI immediately (marker form → pills). The
      // drained re-send hides the user bubble, so this is the only display.
      addLocalMessages(displayForBubble, '', activeConversation?.id, { queued: true } as any);
    } else {
      const targetConversationId = resolveSendConversationId(
        scope,
        activeConversation?.id,
      );
      sendMessage(trimmedContent, targetConversationId, undefined, context, sendOptions);
    }

    // Clear selected edit sections after send and deactivate inspect mode
    if (selectedEditSections.length > 0) {
      setSelectedEditSections([]);
      window.dispatchEvent(new CustomEvent('xavior:inspect-off'));
    }

    setLastMessageTimestamp(); // Track for greeting gap calculation

    // Track sent message so stop-within-5s can repopulate for editing
    lastSentRef.current = { text: trimmedContent, at: Date.now() };

    // Cache the original Files by filename so the contacts.import-csv
    // quick action chip (emitted by the agent in its reply) can look them
    // back up and route them to ImportCSVModal. The composer clears
    // pendingAttachments below, so without this cache the file is gone
    // by the time the chip renders.
    for (const att of pendingAttachments) {
      if (att.file instanceof File) {
        recentlySentFilesRef.current.set(att.filename.toLowerCase(), att.file);
      }
    }
    if (recentlySentFilesRef.current.size > 8) {
      const oldestKey = recentlySentFilesRef.current.keys().next().value;
      if (oldestKey) recentlySentFilesRef.current.delete(oldestKey);
    }

    // Clear form and attachments (don't revoke blob URLs — they're used by sent message previews)
    setPendingAttachments([]);
    // Clear pasted-content chips: they've been snapshotted into
    // `pastedAttachmentsPayload` and sent with the request.
    setPastedAttachments([]);
    setExpandedPastedAttachmentIds(new Set());
    setInputValue('');
    setTimeout(() => {
      if (chatInputRef.current) {
        chatInputRef.current.style.height = 'auto';
        chatInputRef.current.focus();
      }
    }, 0);
  };

  return handleSubmit;
}
